import {
  BusinessHoursFormValues,
  CurationStatus,
  MyProductFormValues,
  ProductCurationStatus,
  ProductStatus,
} from "../../types";
import {
  HasuraInsertBaseVariables,
  HasuraInsertDraftVariables,
  HasuraInsertProductVariables,
} from "../../graphqlQueries/insertProduct";
import {
  getAccessibility,
  getAddress,
  getAvailabilityLanguage,
  getAvailableDays,
  getAvailableMonths,
  getCapacity,
  getCategories,
  getCertificates,
  getDefaultOpeningHours,
  getDuration,
  getImages,
  getLocation,
  getNewProductCurationStatus,
  getOpeningHoursExceptions,
  getPricing,
  getProductInformations,
  getProductType,
  getTargetGroups,
  getVideos,
} from "./productFormDataMapper";

import { BusinessHoursPeriodInsertInput } from "../../graphqlQueries/updateOpeningHours";
import { HasuraUpdateProductVariables } from "../../graphqlQueries/updateProduct";
import { updateArrayWithProductId } from "../formUtils";

const getBusinessHoursId = (formValues: MyProductFormValues) => {
  const { companyHoursEnabled, companyBusinessHoursId, businessHoursId } = formValues;
  if (companyHoursEnabled) {
    return companyBusinessHoursId || undefined;
  } else {
    return (companyBusinessHoursId !== businessHoursId && businessHoursId) || undefined;
  }
};

const baseProduct = (
  formValues: MyProductFormValues,
  companyId: string
): HasuraInsertBaseVariables => {
  const address = getAddress(formValues);
  const location = getLocation(formValues);

  if (!location) {
    address.location = null;
  } else {
    address.location = `(${location.latitude}, ${location.longitude})`;
  }

  return {
    accessible: getAccessibility(formValues.accessible),
    address,
    availabilityLanguages: getAvailabilityLanguage(formValues),
    availableDays: getAvailableDays(formValues),
    availableMonths: getAvailableMonths(formValues),
    businessHoursId: getBusinessHoursId(formValues),
    capacity: getCapacity(formValues),
    certification: getCertificates({
      stfCertified: formValues.stfCertified,
      welcomeCyclistCertified: formValues.welcomeCyclistCertified,
    }),
    companyId,
    duration: getDuration(formValues),
    email: formValues.email,
    images: getImages(formValues),
    phone: formValues.phonenumber,
    pricing: getPricing(formValues),
    productInformations: getProductInformations(formValues),
    socialMediaId: formValues.socialMediaId || null,
    tags: getCategories(formValues.productCategories),
    targetGroups: getTargetGroups(formValues),
    type: getProductType(formValues),
    videos: getVideos(formValues),
  };
};

export const composeDraftProduct = (
  formValues: MyProductFormValues,
  companyId: string
): HasuraInsertDraftVariables => ({
  ...baseProduct(formValues, companyId),
  curationEnabled: false,
  status: ProductStatus.Draft,
});

export const composePublishedProduct = (
  formValues: MyProductFormValues,
  companyId: string
): HasuraInsertProductVariables => ({
  ...baseProduct(formValues, companyId),
  curationEnabled: true,
  curationStatus: CurationStatus.Pending,
  productCurationStatus: ProductCurationStatus.New,
  status: ProductStatus.Published,
});

const baseInsertVariables = (
  data: HasuraInsertProductVariables | HasuraInsertDraftVariables,
  productId: string
) => {
  const updatedCapacities =
    data.capacity && data.capacity[0]
      ? updateArrayWithProductId(data.capacity, productId)
      : undefined;

  const updatedDurations =
    data.duration && data.duration[0]
      ? updateArrayWithProductId(data.duration, productId)
      : undefined;

  return {
    availabilityLanguages: updateArrayWithProductId(data.availabilityLanguages, productId),
    availableMonths: updateArrayWithProductId(data.availableMonths, productId),
    capacity: updatedCapacities && updatedCapacities[0],
    certification: updateArrayWithProductId(data.certification, productId),
    duration: updatedDurations && updatedDurations[0],
    images: updateArrayWithProductId(data.images, productId),
    productInformations: updateArrayWithProductId(data.productInformations, productId),
    tags: updateArrayWithProductId(data.tags, productId),
    targetGroups: updateArrayWithProductId(data.targetGroups, productId),
    videos: updateArrayWithProductId(data.videos, productId),
  };
};

type ComposeUpdatedProductVariables = {
  data: HasuraInsertProductVariables;
  curationStatus: CurationStatus | null;
  productCurationStatus: ProductCurationStatus | null;
  isCurator?: boolean;
  productId: string;
};

export const composeUpdatedProduct = ({
  data,
  curationStatus,
  productCurationStatus,
  productId,
  isCurator,
}: ComposeUpdatedProductVariables): HasuraUpdateProductVariables => {
  const newCurationStatus = curationStatus === null ? CurationStatus.Pending : curationStatus;
  const newProductCurationStatus =
    productCurationStatus === null || curationStatus === null
      ? ProductCurationStatus.New
      : getNewProductCurationStatus(curationStatus, productCurationStatus, isCurator);

  return {
    ...data,
    ...baseInsertVariables(data, productId),
    businessHoursId: data.businessHoursId ?? "",
    curationEnabled: true,
    curationStatus: newCurationStatus,
    id: productId,
    pricing: data.pricing[0],
    productCurationStatus: newProductCurationStatus,
    status: ProductStatus.Published,
    verifiedLinks: [],
  };
};

type ComposeDraftUpdateProductVariables = {
  data: HasuraInsertDraftVariables;
  productId: string;
};

export const composeDraftUpdateProduct = ({
  data,
  productId,
}: ComposeDraftUpdateProductVariables): HasuraUpdateProductVariables => ({
  ...data,
  ...baseInsertVariables(data, productId),
  businessHoursId: data.businessHoursId ?? "",
  curationEnabled: false,
  id: productId,
  pricing: data.pricing[0],
  status: ProductStatus.Draft,
  verifiedLinks: [],
});

export const composeOpeningHoursData = (
  formValues: BusinessHoursFormValues,
  businessHoursId: string
): BusinessHoursPeriodInsertInput[] => {
  return [
    getDefaultOpeningHours(formValues),
    ...getOpeningHoursExceptions(formValues),
  ].map((period) => ({ businessHoursId, ...period }));
};
